import { Component, Input, OnInit, OnChanges, SimpleChanges } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";

// --- (NEW) Define more specific interfaces for clarity and type safety ---
export interface FaqItem {
  question: string;
  answer: string;
}

export interface MachineCategory {
  categoryName: string;
  slug: string;
  introduction: string; // Keep full model for potential future use
  aliases: string[]; // Keep full model for potential future use
  faqs: FaqItem[];
}

export interface FacetData {
  mostPopularMake?: string;
  mostPopularModel?: string;
  mostPopularAttachments?: string[];
  cheapestRentalRate?: number;
  cheapestRentalRateUnit?: string;
}
// --- End of New Interfaces ---

@Component({
  selector: "common-category-faqs",
  templateUrl: "./category-faqs.component.html",
  styleUrls: ["./category-faqs.component.scss"],
})
export class CategoryFaqsComponent implements OnInit, OnChanges {
  @Input() facets?: any; // Accept raw facet data
  // Use the more specific FaqItem interface
  faqs: FaqItem[] = [];
  categoryTitle?: string;
  primaryType: any;
  cityState: any;
  make: string;

  constructor(private http: HttpClient, private route: ActivatedRoute) {}

  ngOnInit() {
    this.primaryType = this.route.snapshot.paramMap.get("primaryType") || null;
    this.cityState = this.route.snapshot.paramMap.get("cityState") || "";
    if (this.cityState) {
      const split = this.cityState.split("-");
      const userState = split[split.length - 1].trim();
      const userCity = split.slice(0, -1).join(" ").trim();
      this.cityState = `${userCity}, ${userState}`; // Update cityState for display
    }

    this.make = this.route.snapshot.paramMap.get("make") || "";

    this.loadFaqs();
  }

  ngOnChanges(changes: SimpleChanges) {
    // This logic remains correct for re-loading when inputs change
    if (changes.categoryName && !changes.categoryName.firstChange) {
      this.loadFaqs();
    }
  }

  private loadFaqs() {
    if (!this.primaryType || this.primaryType === "all") {
      this.faqs = [];
      return;
    }
    const url = `https://company.rubbl.com/pubweb/json/machine/category/machine-faqs.json`;
    this.http.get<MachineCategory[]>(url).subscribe(allCategories => {
      const currentSlug = this.slugify(this.primaryType);
      const foundCategory = allCategories.find(category => category.slug === currentSlug);
      let faqs: FaqItem[] = [];
      if (foundCategory) {
        faqs = foundCategory.faqs || [];
        this.categoryTitle = (foundCategory.categoryName || this.primaryType) + " Frequently Asked Questions";
      } else {
        this.categoryTitle = this.primaryType;
      }
      // Add pre-FAQ questions based on parsed facet data
      const preFaqs = this.generateFacetFaqs(this.primaryType, this.facets);
      this.faqs = [...preFaqs, ...faqs];
    });
  }

  private generateFacetFaqs(categoryName: string, facetData?: any): FaqItem[] {
    if (!facetData) {
      return [];
    }

    if (!this.cityState) {
      return [];
    }

    const faqs: FaqItem[] = [];
    const make = this.make ? this.make.trim() : "";
    const cityState = this.cityState ? this.cityState.trim() : "";
    const makePart = make ? `${make} ` : "";
    const makeLower = make ? make.toLowerCase() : "";
    const categoryWithMake = make ? `${make} ${categoryName}` : categoryName;
    const locationSuffix = cityState ? ` in ${cityState}` : "";

    // Only add most popular makes if make is not selected
    if (!make && Array.isArray(facetData.make) && facetData.make.length > 0) {
      const sortedMakes = [...facetData.make].sort((a, b) => b.count - a.count);
      const topMakes = sortedMakes.slice(0, 3).map(m => m.value);
      const makesList = this.formatListWithOxfordComma(topMakes);
      faqs.push({
        question: `What are the most popular ${categoryName} makes${locationSuffix}?`,
        answer: `The most popular makes for ${categoryName.toLowerCase()} rentals on Rubbl${
          locationSuffix ? " in " + cityState : ""
        } are <strong>${makesList}</strong>. These brands are trusted for their performance and reliability, making them top choices for many operators.`,
      });
    }
    // Most popular models (top 3)
    if (Array.isArray(facetData.model) && facetData.model.length > 0) {
      let filteredModels = facetData.model;
      // If make is selected, filter models by make if possible (if your data supports it)
      // For now, just use the most popular overall
      const sortedModels = [...filteredModels].sort((a, b) => b.count - a.count);
      const topModels = sortedModels.slice(0, 3).map(m => m.value);
      const modelsList = this.formatListWithOxfordComma(topModels);
      faqs.push({
        question: `What are the most popular ${makePart}${categoryName} models${locationSuffix}?`,
        answer: `The most popular models for ${make ? make + " " : ""}${categoryName.toLowerCase()} rentals${
          locationSuffix ? " in " + cityState : ""
        } are <strong>${modelsList}</strong>. These models are highly sought after for their versatility and jobsite performance.`,
      });
    }
    // Most popular attachments (top 3)
    if (Array.isArray(facetData["relatedAttachments/typeDefinition"]) && facetData["relatedAttachments/typeDefinition"].length > 0) {
      const sortedAttachments = [...facetData["relatedAttachments/typeDefinition"]].sort((a, b) => b.count - a.count);
      const topAttachments = sortedAttachments.slice(0, 3).map(a => a.value.trim());
      if (topAttachments.length > 0) {
        const attachmentsList = this.formatListWithOxfordComma(topAttachments);
        faqs.push({
          question: `What are the most popular attachments for a ${makePart}${categoryName}${locationSuffix}?`,
          answer: `The most popular attachments for ${make ? make + " " : ""}${categoryName.toLowerCase()} rentals${
            locationSuffix ? " in " + cityState : ""
          } are <strong>${attachmentsList}</strong>. These attachments help you tackle a wide range of tasks, from digging and grading to lifting and material handling.`,
        });
      }
    }
    // Cheapest rental rate (lowest positive value)
    if (Array.isArray(facetData.rentalRate) && facetData.rentalRate.length > 0) {
      const validRates = facetData.rentalRate.filter(r => typeof r.value === "number" && r.value > 0);
      if (validRates.length > 0) {
        const cheapest = validRates.reduce((a, b) => (a.value < b.value ? a : b));

        const WEEKLY_RATE_PERCENTAGE = 0.3335; // 33.35%
        const DAILY_RATE_PERCENTAGE = 0.15; // 15% // Calculate the estimated rates from the cheapest monthly rate
        const monthlyRate = cheapest.value;
        const estimatedWeeklyRate = Math.round(monthlyRate * WEEKLY_RATE_PERCENTAGE);
        const estimatedDailyRate = Math.round(monthlyRate * DAILY_RATE_PERCENTAGE);
        // Format numbers with commas and no decimals
        const numberFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
        const monthlyRateFormatted = numberFormatter.format(monthlyRate);
        const estimatedWeeklyRateFormatted = numberFormatter.format(estimatedWeeklyRate);
        const estimatedDailyRateFormatted = numberFormatter.format(estimatedDailyRate);
        faqs.push({
          question: `What is the cheapest rental rate for a ${makePart}${categoryName}${locationSuffix}?`,
          answer: `The lowest rental rate for a ${make ? make + " " : ""}${categoryName.toLowerCase()}${
            locationSuffix ? " in " + cityState : ""
          } starts at <strong>$${monthlyRateFormatted}</strong> per month, <strong>$${estimatedWeeklyRateFormatted}</strong> per week, and <strong>$${estimatedDailyRateFormatted}</strong> per day. Actual rates may vary by location, duration, and machine configuration.`,
        });
      }
    }
    return faqs;
  }

  private slugify(str: string): string {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  }

  // Add this helper method to the class:
  private formatListWithOxfordComma(items: string[]): string {
    if (!items || items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
  }
}
